import styled from '@emotion/styled';
import { tokens, Tooltip } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import { getStackedBarChartData } from './dimensions';
import { useAnimateScale } from './useAnimateScale';

const Wrapper = styled.div`
  margin: ${tokens.spacer.x2} 0;
`;

const SVG = styled.svg`
  display: block;
  border-radius: ${tokens.radius.m};
`;

const Legend = styled.div`
  display: grid;
  grid-template-columns: repeat(3, max-content);
  grid-gap: 30px;
  margin-top: ${tokens.spacer.x3};
`;

const Label = styled.div<{ color: string }>`
  border-left: 2px solid ${(p) => p.color};
  padding-left: ${tokens.spacer.x1};
  ${tokens.text('body2')}
`;

const CHART_WIDTH = 708;
const HEIGHT = 80;

const COLORS = [
  tokens.color.crimson_light,
  tokens.BRAND_COLORS.cobalt_500,
  tokens.BRAND_COLORS.mint_500,
];
const LABELS = ['detractors', 'passives', 'promoters'];
const TOOLTIPS = ['detractors-tooltip', 'passives-tooltip', 'promoters-tooltip'];

export interface NPSStackedBarChartProps {
  detractors: number;
  passives: number;
  promoters: number;
}

export function NPSStackedBarChart(props: NPSStackedBarChartProps): JSX.Element | null {
  const { detractors, passives, promoters } = props;

  const isReady = detractors !== undefined || passives !== undefined || promoters !== undefined;

  const scale = useAnimateScale(isReady ? 500 : 0);

  const { t, i18n } = useTranslation('feedback', { keyPrefix: 'results.nps-view-mode' });

  if (!isReady) {
    return null;
  }

  const chartData = getStackedBarChartData([detractors, passives, promoters], CHART_WIDTH);

  return (
    <Wrapper>
      <SVG viewBox={`0 0 ${CHART_WIDTH} ${HEIGHT}`} xmlns="http://www.w3.org/2000/svg">
        <rect
          width={CHART_WIDTH}
          height={HEIGHT}
          fill={tokens.BRAND_COLORS.neutral_100}
          x="0"
          y="0"
        />
        {chartData.map(({ width, x }, index) => (
          <rect
            aria-labelledby={`label-${LABELS[index]}`}
            key={LABELS[index]}
            width={width * scale}
            height={HEIGHT}
            fill={COLORS[index]}
            x={x * scale}
            y="0"
          />
        ))}
      </SVG>
      <Legend>
        {chartData.map((data, index) => (
          <Tooltip key={LABELS[index]} text={t(TOOLTIPS[index])} placement="bottom">
            <Label color={COLORS[index]} id={`label-${LABELS[index]}`}>
              {t(LABELS[index])} <br />
              {data.value.toLocaleString(i18n.language)} (
              <strong>
                {Intl.NumberFormat(i18n.language, {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(data.percentage)}
              </strong>
              )
            </Label>
          </Tooltip>
        ))}
      </Legend>
    </Wrapper>
  );
}
